import { useCallback, useEffect, useState } from 'react'
import { backendClient } from '../../interactors/clients/client'
import { CustomerSubscriptionDtoPlan } from '../../interactors/gen/backendClient'

export type MemberAccessReminderLevel = 'd60' | 'd30' | 'd14' | 'd7' | 'd3' | 'd1' | 'expired'
export type MemberAccessUnlimitedReason = 'premium-subscription' | 'free-trial' | 'stripe-connect'
export type ContentAccessUnlimitedReason =
  | 'grandfathered'
  | 'essential-subscription'
  | 'premium-subscription'
  | 'free-trial'
  | 'stripe-connect'

/** What the organization may store per animal (documents and health follow-up entries). */
export interface ContentAccess {
  isUnlimited: boolean
  unlimitedReason?: ContentAccessUnlimitedReason | null
  /** null when unlimited. */
  maxDocumentsPerAnimal?: number | null
  /** null when unlimited. */
  maxHealthEntriesPerAnimal?: number | null
}

export interface MemberAccessPolicy {
  /**
   * Effective plan: an inactive or expired subscription is already reported as `free`.
   * Optional until the backend shipping it (petso-backend#432) is deployed.
   */
  plan?: CustomerSubscriptionDtoPlan
  /** Optional until the backend shipping it (petso-backend#432) is deployed. */
  content?: ContentAccess
  isUnlimited: boolean
  unlimitedReason?: MemberAccessUnlimitedReason
  /** @deprecated Member limits were removed; always null. */
  maxMembersNumber: number | null
  activeMembersCount: number
  /** @deprecated Member limits were removed; always false. */
  isOverLimit: boolean
  trial: {
    isEligibleForNewPolicy: boolean
    startedAt?: string
    endsAt?: string
    daysRemaining: number | null
    isActive: boolean
    reminderLevel: MemberAccessReminderLevel | null
  }
  stripeConnect: {
    readyForPayments: boolean
    stripeAccountId?: string
    validatedAt?: string
    currentlyUnavailable: boolean
    unavailableSince?: string | null
  }
  lock: {
    appLockedAt?: string | null
    isLocked: boolean
  }
}

const MEMBER_ACCESS_POLICY_CACHE_MS = 30 * 1000

type CacheEntry = {
  policy: MemberAccessPolicy | null
  cachedAt: number
  inflightRequest: Promise<MemberAccessPolicy | null> | null
}

const cacheByCustomerId: Record<string, CacheEntry> = {}

const getCacheEntry = (customerId: string): CacheEntry => {
  if (!cacheByCustomerId[customerId]) {
    cacheByCustomerId[customerId] = {
      policy: null,
      cachedAt: 0,
      inflightRequest: null,
    }
  }

  return cacheByCustomerId[customerId]
}

const fetchPolicy = async (customerId: string, force = false): Promise<MemberAccessPolicy | null> => {
  const cacheEntry = getCacheEntry(customerId)
  const now = Date.now()
  const hasValidCache = !force && cacheEntry.cachedAt > 0 && now - cacheEntry.cachedAt < MEMBER_ACCESS_POLICY_CACHE_MS
  if (hasValidCache) {
    return cacheEntry.policy
  }

  if (cacheEntry.inflightRequest) {
    return cacheEntry.inflightRequest
  }

  cacheEntry.inflightRequest = backendClient
    .get<MemberAccessPolicy>('/customers/member-access-policy')
    .then((response) => {
      cacheEntry.policy = response.data
      cacheEntry.cachedAt = Date.now()
      return cacheEntry.policy
    })
    .catch(() => {
      cacheEntry.cachedAt = Date.now()
      return cacheEntry.policy
    })
    .finally(() => {
      cacheEntry.inflightRequest = null
    })

  return cacheEntry.inflightRequest
}

export const useMemberAccessPolicy = (
  customerId?: string,
  enabled = true
): { policy: MemberAccessPolicy | null; isLoading: boolean; refresh: () => void } => {
  const [policy, setPolicy] = useState<MemberAccessPolicy | null>(customerId ? getCacheEntry(customerId).policy : null)
  const [isLoading, setIsLoading] = useState(false)
  const [refreshIndex, setRefreshIndex] = useState(0)

  const refresh = useCallback(() => {
    setRefreshIndex((prev) => prev + 1)
  }, [])

  useEffect(() => {
    setPolicy(customerId ? getCacheEntry(customerId).policy : null)
    setIsLoading(false)
  }, [customerId])

  useEffect(() => {
    if (!enabled || !customerId) {
      return
    }

    let isMounted = true

    const loadPolicy = async () => {
      setIsLoading(true)
      const policyFromApi = await fetchPolicy(customerId, refreshIndex > 0)
      if (isMounted) {
        setPolicy(policyFromApi)
        setIsLoading(false)
      }
    }

    loadPolicy()

    return () => {
      isMounted = false
    }
  }, [customerId, enabled, refreshIndex])

  return { policy, isLoading, refresh }
}
